import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useNavigate } from 'react-router-dom';

const AdminContext = createContext();

// Update last active every 30 minutes
const LAST_ACTIVE_UPDATE_INTERVAL = 30 * 60 * 1000;

export function AdminProvider({ children }) {
    const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
    const navigate = useNavigate();
    const [isChecking, setIsChecking] = useState(true);
    const [resolvedAdmin, setResolvedAdmin] = useState(undefined);
    const lastActiveUpdatedRef = useRef(false);
    const intervalRef = useRef(null);
    const clerkIdLinkedRef = useRef(false);

    // Query admin by Clerk ID first
    const adminByClerkId = useQuery(
        api.platformAdmins.getAdminByClerkId,
        clerkUser?.id ? { clerkId: clerkUser.id } : 'skip'
    );

    // If not found by Clerk ID, try by email (for pending admins)
    const primaryEmail = clerkUser?.primaryEmailAddress?.emailAddress;
    const adminByEmail = useQuery(
        api.platformAdmins.getAdminByEmail,
        adminByClerkId === null && primaryEmail ? { email: primaryEmail } : 'skip'
    );

    // Update last active
    const updateLastActive = useMutation(api.platformAdmins.updateLastActive);

    // Link Clerk ID mutation
    const linkClerkId = useMutation(api.platformAdmins.linkClerkId);

    // Stable callback for updating last active
    const doUpdateLastActive = useCallback((adminId) => {
        updateLastActive({ adminId }).catch(console.error);
    }, [updateLastActive]);

    // Resolve admin from either query
    useEffect(() => {
        // Found by Clerk ID - use directly
        if (adminByClerkId !== undefined && adminByClerkId !== null) {
            setResolvedAdmin(adminByClerkId);
            return;
        }

        // Not found by Clerk ID, check email lookup
        if (adminByClerkId === null && adminByEmail !== undefined) {
            if (adminByEmail !== null) {
                // Found by email - this is a pending admin, need to link Clerk ID
                setResolvedAdmin(adminByEmail);

                // Link the Clerk ID (only once)
                if (!clerkIdLinkedRef.current && clerkUser?.id && adminByEmail.clerkId?.startsWith('pending_')) {
                    clerkIdLinkedRef.current = true;
                    linkClerkId({ adminId: adminByEmail._id, clerkId: clerkUser.id })
                        .then(() => console.log('✅ Clerk ID linked to admin account'))
                        .catch((err) => console.error('Failed to link Clerk ID:', err));
                }
            } else {
                // Not found by email either - truly unauthorized
                setResolvedAdmin(null);
            }
        }
    }, [adminByClerkId, adminByEmail, clerkUser?.id, linkClerkId]);

    // Handle auth state changes
    useEffect(() => {
        if (!isClerkLoaded) return;

        if (isClerkLoaded && resolvedAdmin === null) {
            // Admin not found in database
            setIsChecking(false);
            navigate('/unauthorized');
        } else if (resolvedAdmin) {
            // Admin found
            setIsChecking(false);
        }
    }, [isClerkLoaded, resolvedAdmin, navigate]);

    // Separate effect for last active updates (throttled)
    useEffect(() => {
        if (!resolvedAdmin?._id) return;

        // Only update once on initial load
        if (!lastActiveUpdatedRef.current) {
            lastActiveUpdatedRef.current = true;
            doUpdateLastActive(resolvedAdmin._id);
        }

        // Set up periodic updates every 30 minutes
        if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
                doUpdateLastActive(resolvedAdmin._id);
            }, LAST_ACTIVE_UPDATE_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [resolvedAdmin?._id, doUpdateLastActive]);

    const hasPrivilege = (privilege) => {
        if (!resolvedAdmin) return false;
        if (resolvedAdmin.privileges.includes('super_admin')) return true;
        return resolvedAdmin.privileges.includes(privilege);
    };

    const isSuperAdmin = () => {
        return resolvedAdmin?.privileges?.includes('super_admin') || false;
    };

    return (
        <AdminContext.Provider
            value={{
                admin: resolvedAdmin,
                isLoading: isChecking || resolvedAdmin === undefined,
                hasPrivilege,
                isSuperAdmin,
            }}
        >
            {children}
        </AdminContext.Provider>
    );
}

export function useAdmin() {
    const context = useContext(AdminContext);
    if (!context) {
        throw new Error('useAdmin must be used within AdminProvider');
    }
    return context;
}

